import phimosisCover from "@/assets/blog/phimosis-sexual-health.jpg.asset.json";
import laserPilesCover from "@/assets/blog/laser-piles-surgery.jpg.asset.json";
import kidneyStonesCover from "@/assets/blog/kidney-stones-prevention.jpg.asset.json";
import safeChildbirthCover from "@/assets/blog/safe-childbirth.jpg.asset.json";
import cataractCover from "@/assets/blog/cataract-surgery.jpg.asset.json";
import varicoseCover from "@/assets/blog/varicose-veins.jpg.asset.json";
import tonsillitisCover from "@/assets/blog/tonsillitis-children.jpg.asset.json";
import kneeReplacementCover from "@/assets/blog/life-after-knee-replacement.jpg.asset.json";

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  authorRole: string;
  date: string; // ISO
  readMinutes: number;
  cover: string;
  body: string; // markdown-ish (paragraphs separated by \n\n, ## for headings)
  tags: string[];
  featured?: boolean;
  // SEO overrides — used verbatim in <title>/meta when present.
  seoTitle?: string;
  seoDescription?: string;
  socialTitle?: string;
  socialDescription?: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "phimosis-sexual-health-guide",
    title: "Phimosis and Sexual Health: Symptoms, Impact & Modern Circumcision Benefits",
    excerpt:
      "A urologist's guide to phimosis, how a tight foreskin affects intimacy and quality of life, and how laser / ZSR-stapler circumcision restores comfort — safely and painlessly.",
    seoTitle: "Phimosis & Sex Life: Painless Laser Circumcision",
    seoDescription:
      "Tight foreskin causing pain, low sensation or infections? A urologist explains phimosis symptoms and how 20-minute laser/ZSR circumcision restores comfort.",
    socialTitle: "Phimosis & Your Sex Life — What Actually Fixes It",
    socialDescription:
      "Pain during sex, recurrent infections, low confidence? See how modern laser & ZSR-stapler circumcision fix phimosis in a single day-care visit.",
    category: "General Surgery",
    author: "Dr. Ankit Verma",
    authorRole: "Senior Consultant, General & Laser Surgery",
    date: "2026-06-24",
    readMinutes: 8,
    cover: phimosisCover.url,
    tags: ["Phimosis", "Circumcision", "Sexual Health", "Laser Surgery", "ZSR Stapler"],
    featured: true,
    body: `Phimosis — a tight foreskin that cannot be pulled back over the head of the penis — is one of the most under-discussed men's health issues in India. It is medically common, easily treatable, and yet most men wait years before speaking to a doctor because the symptoms overlap with intimacy, hygiene and fertility concerns they'd rather not raise. This guide covers what phimosis is, how it affects sexual health, and why modern laser and ZSR-stapler circumcision have made treatment a walk-in, walk-out day-care procedure.

## What is phimosis?

Phimosis is the medical term for a foreskin that is too tight to retract fully. It can be:

- **Physiological** — present from birth and usually resolves by the teenage years.
- **Pathological** — develops later in life due to recurrent infection, scarring (BXO / lichen sclerosus), poor hygiene or forceful retraction.

If the foreskin can be retracted but then gets stuck behind the glans and cannot be pulled forward again, that is called **paraphimosis** — a urological emergency that needs immediate care.

## How phimosis affects sexual health

Because the foreskin is directly involved in erection, sensation and hygiene, a tight foreskin quietly affects almost every aspect of a man's intimate life:

- **Painful erections** — the foreskin cannot stretch during erection, causing tightness, tearing or burning.
- **Painful intercourse (dyspareunia)** — friction on a non-retracting foreskin leads to micro-tears, bleeding and discomfort during and after sex.
- **Reduced sensation** — a permanently covered glans becomes hypersensitive at first and desensitised over time, affecting pleasure for both partners.
- **Recurrent balanitis and UTIs** — trapped moisture and smegma cause repeat inflammation, odour and infections that make intimacy uncomfortable.
- **Performance anxiety** — chronic pain and appearance concerns often lead to reduced confidence, avoidance and relationship stress.
- **Fertility and hygiene** — while phimosis rarely causes infertility directly, repeated infections can affect sperm quality and increase transmission of HPV and other STIs.

If any of these sound familiar, phimosis is very likely the reason — and it is almost always fully reversible with a short surgical procedure.

## Symptoms that mean it's time to see a doctor

- Tight, non-retractable foreskin
- Pain, burning or bleeding during erection or sex
- White scarring or a rigid ring at the tip of the foreskin
- Ballooning of the foreskin during urination
- Repeated balanitis, itching, discharge or foul odour
- Recurrent urinary tract infections
- Difficulty maintaining hygiene despite regular cleaning

## Why circumcision is the definitive fix

Steroid creams and stretching exercises can help mild, early phimosis, but they do not work for scarred (pathological) phimosis and they don't remove the underlying tissue. **Circumcision — surgical removal of the tight foreskin — is the only permanent cure** and is one of the safest, most-studied procedures in surgery.

Documented benefits of modern circumcision include:

- Complete relief from phimosis, paraphimosis and recurrent balanitis
- Longer, more comfortable intercourse — many men report improved control and reduced premature ejaculation after healing
- Better hygiene and dramatically lower risk of UTIs, HPV, HIV and penile cancer
- Reduced risk of cervical cancer and HPV transmission to female partners
- Clean, uniform cosmetic appearance and boosted confidence

## Modern circumcision techniques at Healic

You no longer need stitches, long hospital stays or weeks off work. At Healic we offer three modern, stitch-free options — the surgeon picks the right one for your anatomy and skin type:

- **ZSR-stapler circumcision** — a single-use disposable stapler removes the foreskin and seals the edge in one step. No sutures, minimal bleeding, and a clean cosmetic ring. Ideal for most adults.
- **Laser circumcision** — a precision laser removes the foreskin with almost no bleeding and faster healing. Excellent for patients with diabetes, BXO scarring or sensitive skin.
- **Conventional dorsal-slit circumcision** — reserved for complex cases where stapler or laser is not suitable.

All three are performed under short local or spinal anaesthesia in a single day-care visit.

## What recovery really looks like

- **Day of surgery** — 30–45 minutes in the operation theatre, 2–3 hours in observation, then home.
- **Day 1–2** — mild swelling and dressing changes, managed with oral painkillers and antibiotics.
- **Day 3–5** — back to desk work and light routine.
- **Week 2** — driving, walking and gym allowed.
- **Week 4–6** — full healing; sexual activity resumes with your surgeon's clearance.

Most patients tell us the anticipation was worse than the procedure — the actual downtime is a long weekend.

## Frequently asked questions

**Does circumcision affect sexual pleasure?**  Long-term studies show sexual satisfaction, erectile function and orgasm are unchanged or improved after adult circumcision. Many patients report better control and longer intercourse once the pain of phimosis is gone.

**Is it painful?**  The procedure itself is done under anaesthesia — you feel nothing. Post-operative discomfort is mild and easily managed with tablets for 2–3 days.

**How long before I can have sex again?**  Typically 4–6 weeks, once your surgeon confirms complete healing.

**Is it covered by insurance?**  Yes, when performed for medical reasons (phimosis, recurrent balanitis, BXO) most Indian insurers cover circumcision as day-care surgery. Healic is empanelled with 50+ insurance partners.

**Is it confidential?**  Absolutely. Consultations are private, records are protected, and the entire day-care visit is designed for discretion.

## When to book a consultation

If a tight foreskin is causing pain, repeated infections, or holding back your confidence and intimacy, don't wait years the way most men do. A 15-minute consultation with a Healic urologist or general surgeon will confirm the diagnosis, walk you through the right technique (ZSR, laser or conventional), and give you a fixed-price package with cashless insurance support.

Modern circumcision is safe, stitch-free, back to work in 48 hours — and life-changing for phimosis-related sexual health issues.`,
  },
  {
    slug: "laser-piles-surgery-what-to-expect",

    title: "Laser Piles Surgery: What to Expect Before, During & After",
    excerpt:
      "A simple, painless solution for haemorrhoids — here's a complete walkthrough of laser hemorrhoidoplasty at Healic.",
    category: "General Surgery",
    author: "Dr. Ankit Verma",
    authorRole: "Senior Consultant, General & Laser Surgery",
    date: "2026-05-28",
    readMinutes: 6,
    cover: laserPilesCover.url,
    tags: ["Piles", "Laser Surgery", "Daycare"],
    featured: true,
    body: `Haemorrhoids — commonly called piles — affect 1 in 2 adults at some point. Many delay treatment because of fear of pain or long hospital stays. Modern laser hemorrhoidoplasty (LHP) changes that completely.

## What is laser piles surgery?

A thin laser fibre is gently inserted into the swollen vein. Controlled laser energy shrinks the pile mass from inside — no cuts, no stitches, no open wound.

## What happens on the day of surgery

You're admitted in the morning, the procedure takes 30–40 minutes under short anaesthesia, and most patients are discharged the same evening. You can return to desk work in 2–3 days.

## Recovery timeline

- Day 1–2: mild discomfort, managed with oral painkillers
- Day 3–5: back to most daily activities
- Week 2: full recovery, follow-up consultation

If you've had bleeding or discomfort for more than a few weeks, don't wait. Early consultation makes treatment simpler.`,
  },
  {
    slug: "kidney-stones-prevention-and-treatment",
    title: "Kidney Stones: Why They Form & How Modern Surgery Removes Them Painlessly",
    excerpt:
      "From hydration habits to PCNL — a urologist's guide to preventing and treating kidney stones in 2026.",
    category: "Urology",
    author: "Dr. Rohit Kapoor",
    authorRole: "Consultant Urologist",
    date: "2026-05-14",
    readMinutes: 7,
    cover: kidneyStonesCover.url,
    tags: ["Kidney Stones", "PCNL", "Urology"],
    featured: true,
    body: `Kidney stones are crystallised deposits that form when urine becomes concentrated. Pain in the lower back or side that comes in waves is the classic sign.

## Common causes

- Low daily water intake
- High-salt, high-protein diet
- Family history
- Recurrent UTIs

## When surgery is needed

Stones under 5 mm usually pass with hydration. Stones above 8–10 mm typically need intervention — URS (ureteroscopy), RIRS, or PCNL for larger stones.

## How PCNL works

A keyhole tract is made into the kidney; the stone is fragmented and removed in a single sitting. Hospital stay is 1–2 days and recovery is quick.

Prevention is just as important as treatment: drink 2.5–3 L of water daily, limit salt, and get an annual urine check if you've had a stone before.`,
  },
  {
    slug: "safe-childbirth-at-healic",
    title: "Planning a Safe Childbirth: A Mother's Checklist",
    excerpt:
      "From your first scan to delivery day — a step-by-step guide to a calm, well-supported pregnancy.",
    category: "Gynaecology",
    author: "Dr. Kanika Bhadhkaria",
    authorRole: "Obstetrician & Gynaecologist",
    date: "2026-04-30",
    readMinutes: 8,
    cover: safeChildbirthCover.url,
    tags: ["Pregnancy", "Delivery", "Mother Care"],
    body: `Pregnancy is a journey, and good preparation makes the experience calmer for both mother and baby.

## Trimester-wise care

- First trimester: confirm pregnancy, dating scan, folic acid
- Second trimester: anomaly scan, gestational diabetes screening
- Third trimester: growth scans, delivery planning, birth plan discussion

## On the day

Hospital bag ready by week 36. Know your team — your obstetrician, paediatrician, and anaesthetist. At Healic, our birthing rooms are designed to keep you supported through every contraction.

## After delivery

Lactation support, post-natal exercises, and the 6-week check-up are as important as the delivery itself.`,
  },
  {
    slug: "cataract-surgery-modern-options",
    title: "Cataract Surgery in 2026: Phaco, FLACS & Choosing the Right Lens",
    excerpt:
      "Bladeless, stitch-free, and done in under 20 minutes — here's how modern cataract surgery has transformed.",
    category: "Ophthalmology",
    author: "Dr. Meera Sundaram",
    authorRole: "Consultant Ophthalmologist",
    date: "2026-04-12",
    readMinutes: 5,
    cover: cataractCover.url,
    tags: ["Cataract", "Eye Care", "FLACS"],
    body: `If reading the newspaper has become harder or headlights look hazy at night, cataracts may be the cause.

## Today's options

- **Phacoemulsification**: ultrasound breaks the cataract; the most widely used technique
- **FLACS**: a femtosecond laser does the precise cuts — fully bladeless
- **ECCE**: reserved for very advanced cases

## Choosing your lens (IOL)

Monofocal, multifocal, toric, EDOF — the choice depends on lifestyle, occupation and existing prescription. A good ophthalmologist will walk you through trade-offs honestly.`,
  },
  {
    slug: "varicose-veins-when-to-see-a-doctor",
    title: "Varicose Veins: 6 Signs You Shouldn't Ignore",
    excerpt:
      "Tired, achy legs at the end of the day? It might be more than fatigue. Here's when to get an evaluation.",
    category: "General Surgery",
    author: "Dr. Ankit Verma",
    authorRole: "Senior Consultant, General & Laser Surgery",
    date: "2026-03-22",
    readMinutes: 4,
    cover: varicoseCover.url,
    tags: ["Varicose Veins", "Vascular", "Laser"],
    body: `Varicose veins are enlarged, twisted veins that develop when one-way valves inside the leg veins weaken.

## Watch for

1. Heaviness or aching after standing
2. Visible bulging blue/purple veins
3. Itching around the ankle
4. Night cramps
5. Skin discolouration near the calf
6. A sore that won't heal

## What treatment looks like

Doppler study confirms the diagnosis. Mild cases improve with compression stockings; advanced cases are best treated with endovenous laser ablation (EVLA) — a daycare procedure with no large cuts.`,
  },
  {
    slug: "tonsillitis-in-children-when-to-operate",
    title: "Tonsillitis in Children: When Surgery Becomes the Right Choice",
    excerpt:
      "Recurrent sore throats can disrupt school, sleep, and growth. Here's how ENT specialists decide on tonsillectomy.",
    category: "ENT",
    author: "Dr. Sneha Iyer",
    authorRole: "ENT Consultant",
    date: "2026-03-04",
    readMinutes: 5,
    cover: tonsillitisCover.url,
    tags: ["Tonsils", "Paediatric ENT", "Surgery"],
    body: `Most children get the occasional sore throat. But when episodes become frequent and severe, surgery may be the kinder long-term option.

## The "7-5-3" rule

Tonsillectomy is generally considered if a child has:

- 7 or more documented infections in one year, **or**
- 5 per year over two years, **or**
- 3 per year over three years

## Modern tonsillectomy

Coblation and harmonic scalpel techniques mean less bleeding, faster healing, and most children eat soft food on the same day.`,
  },
  {
    slug: "life-after-knee-replacement",
    title: "Life After Knee Replacement: A Realistic 12-Week Recovery Guide",
    excerpt:
      "What week-by-week recovery actually feels like — and how to make the most of your new joint.",
    category: "Orthopaedics",
    author: "Dr. Sameer Bhalla",
    authorRole: "Joint Replacement Surgeon",
    date: "2026-02-18",
    readMinutes: 6,
    cover: kneeReplacementCover.url,
    tags: ["Knee Replacement", "Orthopaedics", "Rehab"],
    body: `Knee replacement gives back something precious — pain-free walking. But the result depends as much on rehab as on the surgery itself.

## Week-by-week

- **Week 1**: walker-assisted walking, ice, gentle bends
- **Week 2–4**: stair training, stationary cycle, swelling reduces
- **Week 6**: most patients drive again
- **Week 12**: long walks, light sports, full daily routine

## Habits that protect your knee

- Maintain a healthy weight
- Walk daily — never stop moving
- Avoid deep squats and high-impact running

A well-rehabbed knee replacement lasts 20+ years.`,
  },
];

export const blogCategories = Array.from(
  new Set(blogPosts.map((p) => p.category)),
).sort();

export function findPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

export function relatedPosts(slug: string, n = 3): BlogPost[] {
  const post = findPost(slug);
  if (!post) return [];
  return blogPosts
    .filter((p) => p.slug !== slug)
    .sort((a, b) => (a.category === post.category ? -1 : 1))
    .slice(0, n);
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
